import { useState } from 'react'
import InputField from '../inputField'
import Dropdown, { type selectOption } from '#app/components/common/dropdown'
import clsx from 'clsx'
import {
	isNumeric,
	chainOptions,
	removeSpacesFromString,
	isValidSolanaAddress,
} from '#app/utils/misc'
import { CollectionInputDataTypes } from './collectionNFTsInput'
import { InvalidatedNFTInputFields } from './collectionInputModal'

export type TokenListInputData = {
	contract: string
	tokenIDs: string[]
	chain: string
	type: 'smartContractIDs'
	inputSelector: string
}

export const chainSelectionOptions = chainOptions
// .filter(
// 	chain => chain.name !== 'Solana',
// )

type TokenInput = { id: string; value: string }

// FILTER OUT SOLANA BECAUSE IT DOESNT SUPPORT SEQUENTIAL TOKENS
// const chainSelectionOptions = chainNameIDArray.filter(chain => chain.name !== "solana").map(chain => ({
//     name: chain.name,
//     slug: chain.id,
//     title: capitalizeFirstLetter(chain.name),
//     icon: null,
//     version: "1.0.0"
// }))

const TokenListInput = ({
	name = 'CollectionNFTs',
	id,
	slug,
	onChange,
	errorFields,
	inputData,
	isInactive = false,
}: {
	name: string
	onChange: (val: CollectionInputDataTypes, key?: string) => void
	id: string
	slug: string
	isInactive?: boolean
	inputData?: TokenListInputData
	errorFields?: InvalidatedNFTInputFields
}) => {
	const [selectedChain, setSelectedChain] = useState<selectOption | null>(
		inputData && inputData.chain
			? chainSelectionOptions.find(
					chainOption => chainOption.slug === inputData.chain,
				) || null
			: null,
	)
	const [contractAddress, setContractAddress] = useState(
		inputData && inputData.contract ? inputData.contract : '',
	)
	const [tokenList, setTokenList] = useState(
		inputData && inputData.tokenIDs ? inputData.tokenIDs : [],
	)

	const [tokenListString, setTokenListString] = useState(
		inputData && inputData.tokenIDs ? inputData.tokenIDs.toString() : '',
	)

	const handleChainChange = (selected: selectOption) => {
		setSelectedChain(selected)
		setContractAddress('')
		setTokenList([])
		setTokenListString('')
		const res = {
			contract: '',
			tokenIDs: [],
			chain: selected ? selected.slug : '',
			type: 'smartContractIDs' as TokenListInputData['type'],
			inputSelector: `nfts-${id}-${slug}-tokenIDs`,
		}
		onChange(res, 'chain')
	}

	const generateTokenIDList = (chain: string, list: string) => {
		const array = list.split(',')
		const cleanedArray = array
			.map(val => {
				return removeSpacesFromString(val)
			})
			.filter(val => {
				if (chain === 'Solana') {
					return true
				} else {
					return isNumeric(val)
				}
			})
		return cleanedArray || []
	}

	const handleChange = (key: string, val: string | TokenInput) => {
		if (key === 'contract' && typeof val === 'string') {
			setContractAddress(val)
			onChange(
				{
					contract: val,
					tokenIDs: tokenList,
					chain: selectedChain ? selectedChain.slug : '',
					type: 'smartContractIDs' as TokenListInputData['type'],
					inputSelector: `nfts-${id}-${slug}-tokenIDs`,
				},
				key,
			)
		} else if (
			key === 'tokenID' &&
			((val && typeof val === 'string') || val === '')
		) {
			if (val === '') {
				setTokenList([])
				onChange(
					{
						contract: contractAddress,
						tokenIDs: [],
						chain: selectedChain ? selectedChain.slug : '',
						type: 'smartContractIDs' as TokenListInputData['type'],
						inputSelector: `nfts-${id}-${slug}-tokenIDs`,
					},
					key,
				)
			} else {
				const tokenTokenIdListArray = generateTokenIDList(
					selectedChain ? selectedChain.name : '',
					val,
				)
				setTokenList(tokenTokenIdListArray)
				onChange(
					{
						contract: contractAddress,
						tokenIDs: tokenTokenIdListArray,
						chain: selectedChain ? selectedChain.slug : '',
						type: 'smartContractIDs' as TokenListInputData['type'],
						inputSelector: `nfts-${id}-${slug}-tokenIDs`,
					},
					key,
				)
			}

			// setTokenListString(tokenTokenIdListArray.toString())
		}
	}

	const isSolanaSelected = () => {
		return selectedChain === null ||
			(selectedChain && selectedChain.name !== 'Solana')
			? false
			: true
	}

	return (
		<div className={clsx('flex w-full flex-col')}>
			<Dropdown
				containerClasses="mb-2"
				additionalClasses={clsx(
					'h-12',
					errorFields && errorFields.chain ? 'border-red dark:border-red' : '',
				)}
				options={chainSelectionOptions}
				title="Chain"
				placeholder="Select blockchain"
				selected={selectedChain}
				onSelectionChange={(selection: any) => handleChainChange(selection)}
			/>
			<input
				type="string"
				name={`nfts-${id}-${slug}-chain`}
				id={`nfts-${id}-${slug}-chain`}
				hidden={true}
				readOnly={true}
				value={selectedChain ? selectedChain.slug : ''}
			/>
			<InputField
				name="Contract Address"
				additionalClasses="h-12"
				type="string"
				id={`nfts-${id}-${slug}-contract`}
				placeholder="Enter Contract Address"
				containerWidth="100%"
				isInactive={selectedChain === null || isSolanaSelected() ? true : false}
				displayName={true}
				value={contractAddress}
				onChange={e => handleChange('contract', e.currentTarget.value)}
				isError={errorFields && errorFields.contract ? true : false}
				helper={
					selectedChain === null
						? 'Select a chain first'
						: selectedChain.slug === 'solana'
							? 'Contract Address not needed for Solana'
							: errorFields && errorFields.contract
								? errorFields.contract
								: ''
				}
			/>
			<div className="my-2 flex flex-col gap-y-2">
				<InputField
					additionalClasses={'w-full'}
					containerWidth="w-full"
					name="Token ID"
					id={`nfts-${id}-${slug}-tokenIDs`}
					type="string"
					placeholder={'Enter Token ID'}
					displayName={true}
					isInactive={selectedChain === null ? true : false}
					value={tokenListString}
					onChange={e => {
						let newVal = e.currentTarget.value || ''
						newVal = newVal.replace('.', ',')
						newVal = newVal.replace(' ', ',')
						newVal = isSolanaSelected()
							? newVal
							: newVal.replace(/[^0-9 \,]/, '')
						if (
							newVal[newVal.length - 1] === ',' &&
							newVal[newVal.length - 2] === ','
						) {
							console.log('NO double trailing commas')
						} else {
							setTokenListString(newVal)
							handleChange('tokenID', newVal)
						}
					}}
					helper={
						selectedChain === null
							? 'Select a Chain first'
							: errorFields && errorFields.tokenIDs
								? errorFields.tokenIDs
								: selectedChain && selectedChain.name === 'Solana'
									? 'You can enter multiple tokens by adding a comma in between them Ex. Token1,Token2'
									: 'You can add multiple tokens in the same contract using (,). Ex. 1,2,3,4. Only numbers and commas can be entered'
					}
					isError={errorFields && errorFields.tokenIDs ? true : false}
				/>
			</div>
		</div>
	)
}

export default TokenListInput
